import { Fragment, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { IconType } from 'react-icons';

import { createStepHighlightCycle, killStepHighlightTweens } from '../../lib/gsapHighlight';
import SbrIcon from './SbrIcon';

export type HomeProcessStep = {
  title: string;
  body: string;
  Icon: IconType;
};

export type CsProcessStepColor = 'teal' | 'blue' | 'purple';

export type CsProcessStep = {
  n: string;
  title: string;
  body: string;
  Icon: IconType;
  color: CsProcessStepColor;
};

type HomeProps = {
  variant?: 'home';
  steps: HomeProcessStep[];
};

type CsProps = {
  variant: 'cs-process';
  steps: CsProcessStep[];
  /** Pipelining uses "1. Title" in the heading; contingent uses a separate num badge. */
  numberedHeading?: boolean;
};

type Props = HomeProps | CsProps;

const ICON_BG = 'rgba(43, 147, 240, 0.1)';
const ICON_ACTIVE_BG = 'rgba(0, 194, 171, 0.18)';
const ICON_COLOR = '#2b93f0';
const ICON_ACTIVE_COLOR = '#00c2ab';
const ICON_ACTIVE_SHADOW = '0 0 0 2px rgba(0, 194, 171, 0.35), 0 0 20px rgba(0, 194, 171, 0.28)';

const SEGMENT_ACTIVE_BORDER = 'rgba(0, 194, 171, 0.75)';
const SEGMENT_IDLE_BORDER = 'rgba(100, 116, 139, 0.45)';
const CONNECTOR_ACTIVE_BORDER = 'rgba(0, 194, 171, 0.75)';
const CONNECTOR_IDLE_BORDER = 'rgba(0, 194, 171, 0.35)';

export default function StepProcessFlow(props: Props) {
  const variant = props.variant ?? 'home';
  const rootRef = useRef<HTMLDivElement | null>(null);
  const steps = props.steps;

  useGSAP(
    () => {
      if (!rootRef.current) return;

      const isHome = variant === 'home';
      const stepSelector = isHome ? '.home-process-step' : '.cs-process-step';
      const segmentSelector = isHome ? '.home-process__segment' : '.cs-process__connector';
      const iconSelector = isHome ? '.home-process-step__icon' : '.cs-process-step__icon';
      const liftSelector = isHome ? null : '.cs-process-step__card';

      const stepEls = rootRef.current.querySelectorAll<HTMLElement>(stepSelector);
      const segmentEls = rootRef.current.querySelectorAll<HTMLElement>(segmentSelector);
      const iconEls = rootRef.current.querySelectorAll<HTMLElement>(iconSelector);
      if (!stepEls.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let cycleTween: gsap.core.Timeline | null = null;

      const highlightStep = (index: number) => {
        killStepHighlightTweens(stepEls, segmentEls, iconEls);

        stepEls.forEach((step, j) => {
          const icon = step.querySelector<HTMLElement>(iconSelector);
          const liftEl = liftSelector ? step.querySelector<HTMLElement>(liftSelector) : step;
          const isActive = j === index;

          if (icon) {
            if (isActive) {
              if (isHome) {
                gsap.to(icon, {
                  scale: 1.12,
                  backgroundColor: ICON_ACTIVE_BG,
                  color: ICON_ACTIVE_COLOR,
                  boxShadow: ICON_ACTIVE_SHADOW,
                  duration: 0.35,
                  ease: 'back.out(1.6)',
                  overwrite: true,
                });
              } else {
                gsap.to(icon, {
                  scale: 1.12,
                  boxShadow: ICON_ACTIVE_SHADOW,
                  duration: 0.35,
                  ease: 'back.out(1.6)',
                  overwrite: true,
                });
              }
            } else if (isHome) {
              gsap.set(icon, {
                scale: 1,
                backgroundColor: ICON_BG,
                color: ICON_COLOR,
                boxShadow: '0 0 0 0 transparent',
              });
            } else {
              gsap.set(icon, { scale: 1, boxShadow: '0 0 0 0 transparent' });
            }
          }

          if (liftEl) {
            if (isActive) {
              gsap.to(liftEl, { y: -4, duration: 0.35, ease: 'power2.out', overwrite: true });
            } else {
              gsap.set(liftEl, { y: 0 });
            }
          }
        });

        segmentEls.forEach((segment, j) => {
          const isLeading = j === index - 1;
          if (isLeading) {
            gsap.to(segment, {
              opacity: 1,
              borderColor: isHome ? SEGMENT_ACTIVE_BORDER : CONNECTOR_ACTIVE_BORDER,
              duration: 0.35,
              overwrite: true,
            });
          } else {
            gsap.set(segment, {
              opacity: isHome ? 0.45 : 0.5,
              borderColor: isHome ? SEGMENT_IDLE_BORDER : CONNECTOR_IDLE_BORDER,
            });
          }
        });
      };

      const startCycle = () => {
        cycleTween?.kill();
        highlightStep(0);
        if (reducedMotion) return;
        cycleTween = createStepHighlightCycle(steps.length, highlightStep);
      };

      const cleanup = () => {
        cycleTween?.kill();
        cycleTween = null;
      };

      if (reducedMotion) {
        gsap.set(stepEls, { opacity: 1, y: 0, clearProps: 'opacity,transform' });
        gsap.set(segmentEls, { opacity: 1, scaleX: 1, clearProps: 'opacity,transform' });
        startCycle();
        return cleanup;
      }

      gsap.set(stepEls, { opacity: 0, y: 28 });
      gsap.set(segmentEls, { opacity: 0, scaleX: 0, transformOrigin: 'left center' });

      const reveal = gsap.timeline({
        paused: true,
        onComplete: startCycle,
      });

      stepEls.forEach((step, i) => {
        const offset = i * 0.18;
        reveal.to(step, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, offset);

        const segment = segmentEls[i];
        if (segment) {
          reveal.to(segment, { opacity: 1, scaleX: 1, duration: 0.45, ease: 'power2.out' }, offset + 0.12);
        }
      });

      let revealStarted = false;
      const playReveal = () => {
        if (revealStarted) return;
        revealStarted = true;
        reveal.play();
      };

      const scrollTrigger = ScrollTrigger.create({
        trigger: rootRef.current,
        start: 'top 82%',
        onEnter: playReveal,
        onEnterBack: playReveal,
      });

      if (ScrollTrigger.isInViewport(rootRef.current)) {
        playReveal();
      }

      return () => {
        scrollTrigger.kill();
        reveal.kill();
        cleanup();
      };
    },
    { scope: rootRef, dependencies: [steps.length, variant] },
  );

  if (variant === 'home') {
    const homeSteps = steps as HomeProcessStep[];
    return (
      <div ref={rootRef} className="home-process px-lg-2">
        <div className="home-process__steps">
          {homeSteps.map((step, index) => (
            <Fragment key={step.title}>
              <article className="home-process-step">
                <div className="home-advantage-card__icon home-process-step__icon" aria-hidden>
                  <SbrIcon icon={step.Icon} size={24} />
                </div>
                <h3 className="home-process-step__title">{step.title}</h3>
                <p className="home-process-step__body">{step.body}</p>
              </article>
              {index < homeSteps.length - 1 && <div className="home-process__segment" aria-hidden />}
            </Fragment>
          ))}
        </div>
      </div>
    );
  }

  const csSteps = steps as CsProcessStep[];
  const numberedHeading = (props as CsProps).numberedHeading ?? false;

  return (
    <div ref={rootRef} className="cs-process px-lg-2">
      <div className="cs-process__track">
        {csSteps.map((step, index) => (
          <Fragment key={step.n}>
            <article className={`cs-process-step cs-process-step--${step.color}`}>
              <div
                className={`cs-process-step__card${
                  numberedHeading ? ' cs-process-step__card--numbered' : ''
                }`}
              >
                <div className="cs-process-step__icon" aria-hidden>
                  <SbrIcon icon={step.Icon} size={26} />
                </div>
                {numberedHeading ? (
                  <h3 className="cs-process-step__heading">
                    {step.n}. {step.title}
                  </h3>
                ) : (
                  <>
                    <div className="cs-process-step__num">{step.n}</div>
                    <h3 className="cs-process-step__heading">{step.title}</h3>
                  </>
                )}
                <p className="cs-process-step__body">{step.body}</p>
              </div>
            </article>
            {index < csSteps.length - 1 && <div className="cs-process__connector" aria-hidden />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
